/**
 * @file enrollmentScript.ts
 * @brief The voice enrollment teleprompter: three paragraphs to read, then free
 *        speech. One recording, ~61 seconds.
 *
 * @details
 * A copy of the phone's script (FRONTEND enrollmentKaraoke.ts,
 * buildEnrollmentKaraokeCues) so a voice registered on the web is the same
 * sample as one registered on the phone. Change both together.
 *
 * @author DES
 * @date 04-10-2026
 */

export type EnrollmentCue = {
  id: string;
  kind: "read" | "free_speech";
  text: string;
  emphasisWords: string[];
  durationSec: number;
};

export type EnrollmentCueState = {
  index: number;
  cue: EnrollmentCue;
  cueElapsedSec: number;
  cueRemainingSec: number;
  /** 0–1 across the whole script. */
  progress: number;
  complete: boolean;
};

export const FREE_SPEECH_HINT = "Speak in your language: a few sentences.";
export const FREE_SPEECH_NOTE = "Urdu, English, Arabic, Hindi, or any mix.";

/** Backend floor and ceiling (voiceEnrollment.service.js). */
export const ENROLLMENT_MIN_SECONDS = 61;
export const ENROLLMENT_MAX_SECONDS = 90;

export function buildEnrollmentCues(firstName: string, lastName: string): EnrollmentCue[] {
  const name = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ") || "…";
  return [
    {
      id: "opening-scene",
      kind: "read",
      text: `My name is ${name}. On Monday morning I checked seven thick maps while the old boat sailed under a golden moon.`,
      emphasisWords: [firstName.trim() || name, "Monday", "seven", "thick", "old", "golden", "moon"],
      durationSec: 14,
    },
    {
      id: "valley-voices",
      kind: "read",
      text: "She chose thick vines near the sheep while fresh voices carried softly through the sunny valley below.",
      emphasisWords: ["She", "chose", "thick", "vines", "sheep", "voices", "softly", "sunny", "valley"],
      durationSec: 13,
    },
    {
      id: "count-and-leave",
      kind: "read",
      text: "We counted one, two, three, four, five, six, seven, eight, nine, and ten before the team packed every bag at dawn.",
      emphasisWords: ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "packed", "dawn"],
      durationSec: 13,
    },
    { id: "native-language", kind: "free_speech", text: "", emphasisWords: [], durationSec: 21 },
  ];
}

export function totalCueSeconds(cues: EnrollmentCue[]): number {
  return cues.reduce((sum, c) => sum + c.durationSec, 0);
}

export function resolveCueState(elapsedSec: number, cues: EnrollmentCue[]): EnrollmentCueState {
  const total = totalCueSeconds(cues);
  let cursor = 0;
  for (let i = 0; i < cues.length; i += 1) {
    const cue = cues[i]!;
    const end = cursor + cue.durationSec;
    if (elapsedSec < end) {
      return {
        index: i,
        cue,
        cueElapsedSec: elapsedSec - cursor,
        cueRemainingSec: Math.max(0, Math.ceil(end - elapsedSec)),
        progress: Math.min(1, elapsedSec / total),
        complete: false,
      };
    }
    cursor = end;
  }
  const last = cues[cues.length - 1]!;
  return {
    index: cues.length - 1,
    cue: last,
    cueElapsedSec: last.durationSec,
    cueRemainingSec: 0,
    progress: 1,
    complete: true,
  };
}

/** Split a line into plain and emphasised runs (the phone's HighlightedLine). */
export function splitEmphasis(text: string, words: string[]): { text: string; emphasis: boolean }[] {
  if (!words.length) return [{ text, emphasis: false }];
  const pattern = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const regex = new RegExp(`\\b(${pattern})\\b`, "gi");
  const out: { text: string; emphasis: boolean }[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), emphasis: false });
    out.push({ text: m[0], emphasis: true });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), emphasis: false });
  return out;
}
